import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Radar, ArrowRight, Sparkles } from "lucide-react";

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [discordUsername, setDiscordUsername] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    const dh = discordUsername.trim();
    if (dh.length < 2) {
      setError("Discord username is required so we can reach you in the CardRadar Discord");
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, name || null, dh);
      // New signups land on the admin waitlist; the waitlist page explains next steps.
      navigate("/waitlist", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 mb-8">
          <div className="relative w-9 h-9 border border-[#EAB308] flex items-center justify-center bg-black">
            <Radar className="w-5 h-5 text-[#EAB308]" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base font-black tracking-tighter uppercase">
              Card<span className="text-[#EAB308]">Radar</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#737373] font-mono -mt-0.5">
              TCG Market Scanner
            </div>
          </div>
        </Link>

        <h1 className="font-heading text-3xl font-black tracking-tighter uppercase mb-2">
          Get started
        </h1>
        <p className="text-sm text-[#A3A3A3] mb-8">
          Create an account. Subscription required to use the scanner.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Name (optional)</div>
            <input
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ash Ketchum"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Email *</div>
            <input
              data-testid="register-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">Password * (min 8)</div>
            <input
              data-testid="register-password-input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1.5">
              Discord username * <span className="text-[#525252] tracking-normal font-normal normal-case">(we ping you here once approved)</span>
            </div>
            <input
              data-testid="register-discord-input"
              required
              minLength={2}
              maxLength={64}
              value={discordUsername}
              onChange={(e) => setDiscordUsername(e.target.value)}
              placeholder="yourhandle  or  yourhandle#1234"
              className="w-full bg-[#0d0d0d] border border-[#262626] text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#EAB308] focus:ring-1 focus:ring-[#EAB308] placeholder:text-[#525252]"
            />
          </div>
          {error && (
            <div data-testid="register-error" className="text-xs text-[#EF4444] bg-[#EF4444]/5 border border-[#EF4444]/30 px-3 py-2 rounded-sm">
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid="register-submit-btn"
            disabled={submitting}
            className="w-full bg-[#EAB308] text-black font-bold uppercase tracking-wider text-xs py-3 rounded-sm hover:bg-[#FDE047] transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_0_18px_rgba(234,179,8,0.2)]"
          >
            <Sparkles className="w-4 h-4" />
            {submitting ? "Creating…" : "Create account"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <p className="text-center text-xs text-[#737373] mt-8">
          Already have one?{" "}
          <Link to="/login" className="text-[#EAB308] hover:text-[#FDE047] font-bold" data-testid="link-login">
            Sign in →
          </Link>
        </p>
      </div>
    </div>
  );
}
